"use client";

import { AddCellLoading } from "./add-replace-cell/loading";
import { AddCellSuccess } from "./add-replace-cell/success-new";
import { AlertStepCard } from "./add-replace-cell/alert-step-card";
import { DeleteCellsLoading } from "./delete-cells/loading";
import { DeleteCellsSuccess } from "./delete-cells/success";
import { AddDrawingsLoading } from "./add-drawings/loading";
import { AddDrawingsSuccess } from "./add-drawings/success";
import { DeleteDrawingsLoading } from "./delete-drawings/loading";
import { DeleteDrawingsSuccess } from "./delete-drawings/success";
import { SetViewportLoading } from "./set-viewport/loading";
import { SetViewportSuccess } from "./set-viewport/success";
import { CreatePlanLoading } from "./create-plan/loading";
import { CreatePlanSuccess } from "./create-plan/success";
import {
  PlanDisplay,
  ConsultPlanningAgentLoading,
} from "./consult-planning-agent/loading";
import { UnifiedSearchLoading } from "./unified-search/loading";
import { UnifiedSearchSuccess } from "./unified-search/success";
import { UnifiedSearchChatSuccess } from "./unified-search/chat-success";
import { GetPolygonDocsLoading } from "./get-polygon-docs/loading";
import { GetPolygonDocsSuccess } from "./get-polygon-docs/success";
import { GetStockScreenerDocsLoading } from "./get-stock-screener-docs/loading";
import { GetStockScreenerDocsSuccess } from "./get-stock-screener-docs/success";
import { GetVisualTemplatesLoading } from "./get-visual-templates/loading";
import { GetVisualTemplatesSuccess } from "./get-visual-templates/success";
import { GetCellDetailsLoading } from "./get-cell-details/loading";
import { GetCellDetailsSuccess } from "./get-cell-details/success";
import { CreateNewRunLoading } from "./create-new-run/loading";
import { CreateNewRunSuccess } from "./create-new-run/success";
import { ExecuteAllCellsLoading } from "./execute-all-cells/loading";
import { ExecuteAllCellsSuccess } from "./execute-all-cells/success";
import { AddPlanStepLoading } from "./add-plan-step/loading";
import { AddPlanStepSuccess } from "./add-plan-step/success";
import { DeletePlanStepLoading } from "./delete-plan-step/loading";
import { DeletePlanStepSuccess } from "./delete-plan-step/success";
import { ErrorLoggingDisplay } from "./error-logging/display";
import { FastForwardButton } from "./fast-forward/button";
import { FastForwardLoading } from "./fast-forward/loading";
import { FastForwardSuccess } from "./fast-forward/success";
import { AwaitUserExecutionButton } from "./await-user-execution/button";
import { TickerSearchLoading } from "./ticker-search/loading";
import { TickerSearchSuccess } from "./ticker-search/success";
import { SearchDocsLoading } from "./search-docs/loading";
import { SearchDocsSuccess } from "./search-docs/success";
import { GenerateSummaryLoading } from "./generate-summary/loading";
import { GenerateSummarySuccess } from "./generate-summary/success";
import { RestartNotebookLoading } from "./restart-notebook/loading";
import { RestartNotebookSuccess } from "./restart-notebook/success";
import { AskUserDisplay } from "./ask-user/display";
import { ProposeSkillDisplay } from "./propose-skill/display";
import {
  UploadAlertDisplay,
  DeleteAlertDisplay,
  EditAlertDisplay,
  ForkAlertDisplay,
  CreateAlertDisplay,
} from "./alerts/display";
import { SwitchModeDisplay } from "./switch-mode/display";
import { GetSkillDisplay } from "./get-skill/display";
import {
  isToolCancelled,
  getCancellationText,
  isMaxIterations,
  isNotebookBusy,
} from "@/lib/tool-utils";
import { GenericCancellation } from "./generic-cancellation";
import { NotebookBusyNotice } from "./notebook-busy";

interface ToolDisplayProps {
  toolName: string;
  status: "loading" | "success" | "error";
  result?: any;
  chatId: string;
  args?: any;
  isReadonly?: boolean;
  toolCallId?: string;
  onContinue?: () => void; // Callback for continue action
  isLastMessage?: boolean; // Only show button if this is the last message
  isCompleted?: boolean; // Show completed state after continue is clicked
  isArtifactVisible?: boolean; // Whether artifact is visible
  chartRef?: any; // Chart reference for drawing tools
  isStreaming?: boolean; // Whether the chat is actively streaming
  blurContent?: boolean; // Free preview: blur cell content but keep title
  viewMode?: string; // Current session view_mode ("research" | "alert")
  alertStartCellIndex?: number | null; // First cell index that counts as an alert step
}

export function ToolDisplay({
  toolName,
  status,
  chatId,
  isReadonly,
  result,
  args,
  toolCallId,
  onContinue,
  isLastMessage,
  isCompleted,
  isArtifactVisible = false,
  chartRef,
  isStreaming = false,
  blurContent = false,
  viewMode,
  alertStartCellIndex,
}: ToolDisplayProps) {
  // DEFENSIVE CANCELLATION CHECK - applies to ALL tools
  // Check for cancellation BEFORE any tool-specific logic
  if (status === "error" && isToolCancelled(result)) {
    return (
      <GenericCancellation
        toolName={toolName}
        result={result}
        onContinue={onContinue}
        isLastMessage={isLastMessage}
        isCompleted={isCompleted}
        viewMode={viewMode}
      />
    );
  }

  if (isNotebookBusy(result)) {
    return <NotebookBusyNotice toolName={toolName} viewMode={viewMode} />;
  }

  // propose_skill owns its own state (editable draft, save, saved, validation
  // errors), so route it BEFORE the generic error handler. A rejected draft
  // (result.status="error") still renders the editable card from args so the
  // user can fix + Save while the agent also re-drafts.
  if (toolName === "propose_skill") {
    return (
      <ProposeSkillDisplay
        sessionId={chatId}
        args={args}
        result={result}
        status={status === "loading" ? "loading" : "success"}
        toolCallId={toolCallId}
        isReadonly={isReadonly}
        viewMode={viewMode}
      />
    );
  }

  if (result?.status === "error") {
    return (
      <ErrorLoggingDisplay
        result={result}
        onContinue={onContinue}
        isLastMessage={isLastMessage}
        isCompleted={isCompleted}
        viewMode={viewMode}
      />
    );
  }

  // Extract step index from result if available
  const rawStepIndex: number | undefined =
    result?.execution_result?.cell_info?.index;
  const stepIndex = rawStepIndex ?? 1;
  const cellInfo = result?.execution_result?.cell_info;
  // Fallback: execution_result.outputs when cell_info.outputs is empty
  const fallbackOutputs = result?.execution_result?.outputs;

  // Helper: are we in alert mode AND does this cell fall within the alert range?
  // Computes the alert-relative step number (1-based) for the AlertStepCard.
  // When null, the AlertStepCard omits the number (e.g. during `add_cell`
  // loading where the kernel hasn't assigned an index yet).
  const isAlertMode = viewMode === "alert" || viewMode === "signal";
  const rawCellIndex =
    cellInfo?.cell_index ??
    cellInfo?.index ??
    result?.cell_index ??
    args?.cell_index;
  let alertStepNumber: number | null = null;
  if (
    isAlertMode &&
    typeof rawCellIndex === "number" &&
    typeof alertStartCellIndex === "number"
  ) {
    alertStepNumber = Math.max(1, rawCellIndex - alertStartCellIndex + 1);
  } else if (isAlertMode && typeof rawStepIndex === "number" && rawStepIndex > 0) {
    // Only use stepIndex when it came from a real cell_info.index — never the
    // `?? 1` fallback, which would render a misleading "step 1" on every
    // add_cell loading card.
    alertStepNumber = rawStepIndex;
  }
  const alertPurpose: string =
    cellInfo?.purpose ||
    (typeof args?.purpose === "string" ? args.purpose : "") ||
    "";

  // Route to specific tool components based on toolName and status
  if (toolName === "add_cell") {
    if (status === "loading") {
      if (isAlertMode) {
        return (
          <AlertStepCard
            signal={viewMode === "signal"}
            stepNumber={alertStepNumber}
            purpose={alertPurpose}
            loading
            action="add"
          />
        );
      }
      return (
        <AddCellLoading
          type={args?.type}
          viewMode={viewMode}
          // Live sessions only — replays/shared snapshots (isReadonly) use a
          // different progress endpoint and shouldn't poll from here.
          sessionId={isReadonly ? undefined : chatId}
        />
      );
    }

    // Handle both success and canceled states in the same component
    if (status === "success" || result?.status === "canceled") {
      if (isAlertMode && result?.status !== "canceled") {
        return (
          <AlertStepCard
            signal={viewMode === "signal"}
            stepNumber={alertStepNumber}
            purpose={alertPurpose}
            loading={false}
            action="add"
          />
        );
      }
      return (
        <AddCellSuccess
          stepIndex={stepIndex}
          cellInfo={cellInfo}
          sessionId={chatId}
          result={result} // Pass the full result to handle canceled state
          hideContent={isArtifactVisible}
          blurContent={blurContent}
          fallbackOutputs={fallbackOutputs}
          triggerRefresh={Boolean(
            isLastMessage && isStreaming && result?.status !== "canceled",
          )}
          viewMode={viewMode}
        />
      );
    }
  }

  if (toolName === "replace_cell") {
    if (status === "loading") {
      if (isAlertMode) {
        return (
          <AlertStepCard
            signal={viewMode === "signal"}
            stepNumber={alertStepNumber}
            purpose={alertPurpose}
            loading
            action="replace"
          />
        );
      }
      return <AddCellLoading type="replace" viewMode={viewMode} />;
    }
    if (status === "success" || result?.status === "canceled") {
      if (isAlertMode && result?.status !== "canceled") {
        return (
          <AlertStepCard
            signal={viewMode === "signal"}
            stepNumber={alertStepNumber}
            purpose={alertPurpose}
            loading={false}
            action="replace"
          />
        );
      }
      return (
        <AddCellSuccess
          type="replace"
          stepIndex={stepIndex}
          cellInfo={cellInfo}
          sessionId={chatId}
          result={result}
          hideContent={isArtifactVisible}
          blurContent={blurContent}
          fallbackOutputs={fallbackOutputs}
          triggerRefresh={Boolean(
            isLastMessage && isStreaming && result?.status !== "canceled",
          )}
          viewMode={viewMode}
        />
      );
    }
  }

  if (toolName === "edit_cell") {
    if (status === "loading") {
      if (isAlertMode) {
        return (
          <AlertStepCard
            signal={viewMode === "signal"}
            stepNumber={alertStepNumber}
            purpose={alertPurpose}
            loading
            action="edit"
          />
        );
      }
      return <AddCellLoading type="edit" viewMode={viewMode} />;
    }
    if (status === "success" || result?.status === "canceled") {
      if (isAlertMode && result?.status !== "canceled") {
        return (
          <AlertStepCard
            signal={viewMode === "signal"}
            stepNumber={alertStepNumber}
            purpose={alertPurpose}
            loading={false}
            action="edit"
          />
        );
      }
      return (
        <AddCellSuccess
          type="edit"
          stepIndex={stepIndex}
          cellInfo={cellInfo}
          sessionId={chatId}
          result={result}
          hideContent={isArtifactVisible}
          blurContent={blurContent}
          fallbackOutputs={fallbackOutputs}
          triggerRefresh={Boolean(
            isLastMessage && isStreaming && result?.status !== "canceled",
          )}
          viewMode={viewMode}
        />
      );
    }
  }

  if (toolName === "delete_cells") {
    if (status === "loading") {
      return <DeleteCellsLoading viewMode={viewMode} />;
    }
    if (status === "success") {
      return <DeleteCellsSuccess stepIndex={result?.stepIndex} viewMode={viewMode} />;
    }
  }

  if (toolName === "add_drawings") {
    if (status === "loading") {
      return <AddDrawingsLoading args={args} viewMode={viewMode} />;
    }
    if (status === "success") {
      return (
        <AddDrawingsSuccess
          args={args}
          result={result}
          chartRef={chartRef}
          viewMode={viewMode}
        />
      );
    }
  }

  if (toolName === "delete_drawings") {
    if (status === "loading") {
      return <DeleteDrawingsLoading args={args} viewMode={viewMode} />;
    }
    if (status === "success") {
      return (
        <DeleteDrawingsSuccess
          args={args}
          result={result}
          chartRef={chartRef}
          viewMode={viewMode}
        />
      );
    }
  }

  if (toolName === "set_viewport") {
    if (status === "loading") {
      return <SetViewportLoading args={args} viewMode={viewMode} />;
    }
    if (status === "success") {
      return (
        <SetViewportSuccess
          args={args}
          result={result}
          chartRef={chartRef}
          viewMode={viewMode}
        />
      );
    }
  }

  if (toolName === "restart_notebook") {
    if (status === "loading") {
      return <RestartNotebookLoading viewMode={viewMode} />;
    }
    if (status === "success") {
      return <RestartNotebookSuccess result={result} viewMode={viewMode} />;
    }
  }

  if (toolName === "create_plan") {
    if (status === "loading") {
      return <CreatePlanLoading viewMode={viewMode} />;
    }
    if (status === "success") {
      return (
        <CreatePlanSuccess
          stepIndex={stepIndex}
          sessionId={chatId}
          result={result}
          viewMode={viewMode}
        />
      );
    }
    // Cancellation is handled by the defensive check above
  }

  if (toolName === "consult_planning_agent") {
    if (status === "loading") {
      return <ConsultPlanningAgentLoading sessionId={chatId} viewMode={viewMode} />;
    }
    if (status === "success") {
      if (isToolCancelled(result)) {
        return (
          <GenericCancellation
            toolName={toolName}
            result={result}
            onContinue={onContinue}
            isLastMessage={isLastMessage}
            isCompleted={isCompleted}
            viewMode={viewMode}
          />
        );
      }
      return (
        <ConsultPlanningAgentLoading
          sessionId={chatId}
          isComplete
          result={result}
          viewMode={viewMode}
        />
      );
    }
  }

  if (toolName === "fast_forward") {
    if (status === "loading") {
      return <FastForwardLoading state="loading" viewMode={viewMode} />;
    }
    if (status === "success") {
      return (
        <FastForwardButton
          sessionId={chatId}
          toolCallId={toolCallId}
          args={args}
          viewMode={viewMode}
        />
      );
    }
  }

  if (toolName === "await_user_execution") {
    if (status === "loading") {
      return (
        <AwaitUserExecutionButton
          sessionId={chatId}
          toolCallId={toolCallId}
          args={args}
          viewMode={viewMode}
        />
      );
    }
  }

  if (toolName === "ask_user") {
    return (
      <AskUserDisplay
        sessionId={chatId}
        toolCallId={toolCallId}
        args={args}
        result={result}
        status={status === "loading" ? "loading" : "success"}
        // In replay / shared-snapshot mode the card hides "Skip" +
        // free-text input so the only clickable thing is one of the
        // canonical pre-selected options. Live chats still see both.
        isReadonly={isReadonly}
        signal={viewMode === "signal"}
        viewMode={viewMode}
      />
    );
  }

  if (toolName === "upload_alert") {
    return (
      <UploadAlertDisplay
        status={status === "loading" ? "loading" : "success"}
        result={result}
        args={args}
        sessionId={chatId}
        toolCallId={toolCallId}
        isReadonly={isReadonly}
        viewMode={viewMode}
      />
    );
  }

  if (toolName === "delete_alert") {
    return (
      <DeleteAlertDisplay
        status={status === "loading" ? "loading" : "success"}
        result={result}
        args={args}
        sessionId={chatId}
        toolCallId={toolCallId}
        viewMode={viewMode}
      />
    );
  }

  if (toolName === "edit_alert") {
    return (
      <EditAlertDisplay
        status={status === "loading" ? "loading" : "success"}
        result={result}
        args={args}
        sessionId={chatId}
        toolCallId={toolCallId}
        viewMode={viewMode}
      />
    );
  }

  if (toolName === "fork_alert") {
    return (
      <ForkAlertDisplay
        status={status === "loading" ? "loading" : "success"}
        result={result}
        args={args}
        sessionId={chatId}
        toolCallId={toolCallId}
        viewMode={viewMode}
      />
    );
  }

  if (toolName === "create_alert") {
    return (
      <CreateAlertDisplay
        status={status === "loading" ? "loading" : "success"}
        result={result}
        args={args}
        sessionId={chatId}
        toolCallId={toolCallId}
        viewMode={viewMode}
      />
    );
  }

  if (toolName === "switch_mode") {
    return (
      <SwitchModeDisplay
        status={status === "loading" ? "loading" : "success"}
        result={result}
        args={args}
      />
    );
  }

  if (toolName === "get_skill") {
    return (
      <GetSkillDisplay
        status={status === "loading" ? "loading" : "success"}
        result={result}
        args={args}
      />
    );
  }

  if (toolName === "ticker_search") {
    if (status === "loading") {
      return <TickerSearchLoading viewMode={viewMode} />;
    }
    if (status === "success") {
      return <TickerSearchSuccess result={result} viewMode={viewMode} />;
    }
  }

  if (toolName === "search_docs") {
    if (status === "loading") {
      return <SearchDocsLoading viewMode={viewMode} />;
    }
    if (status === "success") {
      return <SearchDocsSuccess result={result} viewMode={viewMode} />;
    }
  }

  if (toolName === "generate_summary") {
    if (status === "loading") {
      return <GenerateSummaryLoading viewMode={viewMode} />;
    }
    if (status === "success" || result?.status === "canceled") {
      return (
        <GenerateSummarySuccess
          sessionId={chatId}
          result={result}
          hideContent={isArtifactVisible}
          triggerRefresh={Boolean(
            isLastMessage && isStreaming && result?.status !== "canceled",
          )}
          viewMode={viewMode}
        />
      );
    }
  }

  if (toolName === "user_message") {
    // Silent execution - no UI display, just return null
    // The actual logic will be handled in the Chat component
    return null;
  }

  switch (toolName) {
    case "update_plan":
      if (status === "loading") return null;
      if (status === "success") {
        return <PlanDisplay result={result} mode="progress" viewMode={viewMode} />;
      }
      break;

    case "get_plan":
      if (status === "success") {
        return <PlanDisplay result={result} mode="progress" viewMode={viewMode} />;
      }
      return null;

    case "unified_search":
      return status === "loading" ? (
        <UnifiedSearchLoading viewMode={viewMode} />
      ) : (
        <UnifiedSearchChatSuccess result={result} viewMode={viewMode} />
      );

    case "get_documentation":
      return status === "loading" ? (
        <GetPolygonDocsLoading viewMode={viewMode} />
      ) : (
        <GetPolygonDocsSuccess result={result} viewMode={viewMode} />
      );

    case "get_visual_templates":
      return status === "loading" ? (
        <GetVisualTemplatesLoading viewMode={viewMode} />
      ) : (
        <GetVisualTemplatesSuccess result={result} viewMode={viewMode} />
      );

    case "get_cell_details":
      return status === "loading" ? (
        <GetCellDetailsLoading viewMode={viewMode} />
      ) : (
        <GetCellDetailsSuccess result={result} viewMode={viewMode} />
      );

    case "create_new_run":
      return status === "loading" ? (
        <CreateNewRunLoading viewMode={viewMode} />
      ) : (
        <CreateNewRunSuccess result={result} viewMode={viewMode} />
      );

    case "execute_all_cells":
      return status === "loading" ? (
        <ExecuteAllCellsLoading viewMode={viewMode} />
      ) : (
        <ExecuteAllCellsSuccess result={result} viewMode={viewMode} />
      );

    case "add_plan_step":
      return status === "loading" ? (
        <AddPlanStepLoading viewMode={viewMode} />
      ) : (
        <AddPlanStepSuccess result={result} viewMode={viewMode} />
      );

    case "delete_plan_step":
      return status === "loading" ? (
        <DeletePlanStepLoading viewMode={viewMode} />
      ) : (
        <DeletePlanStepSuccess result={result} viewMode={viewMode} />
      );

    case "get_xynth_market_screener_docs":
      return status === "loading" ? (
        <GetStockScreenerDocsLoading viewMode={viewMode} />
      ) : (
        <GetStockScreenerDocsSuccess result={result} viewMode={viewMode} />
      );

    case "error_logging_tool":
      // Handle error logging tool with different states
      return (
        <ErrorLoggingDisplay
          result={result}
          onContinue={onContinue}
          isLastMessage={isLastMessage}
          isCompleted={isCompleted}
          viewMode={viewMode}
        />
      );
  }

  return null;
}

export { FastForwardLoading, FastForwardSuccess, ErrorLoggingDisplay };
